import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';
import { IsafDownload } from './reports';
import { VatReturn } from './taxes';

interface Settings { vatPayer: boolean; vatPeriod: 'month' | 'half_year'; employees: boolean; dividends: boolean; euSupplies: boolean; psdMembers: number; vatDeductionPercent?: number }

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toLocaleDateString('sv-SE').slice(0, 7); };

/**
 * Deklaracijos: the declaration files of a month in one place — FR0600 and i.SAF (VAT payers), SAM and GPM313
 * (payroll, dividends) — and the year's profit tax return and financial statements.
 */
export function DeclarationsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [month, setMonth] = useState(lastMonth);
  const [error, setError] = useState<unknown>(null);
  const taxes = useQuery({ queryKey: ['taxes', 'settings-only'], queryFn: () => api.get<{ settings: Settings }>('api/v1/taxes') });
  const payroll = useQuery({ queryKey: ['payroll', month], queryFn: () => api.get<{ run: { id: number; status: string; paidOn: string | null } | null }>(`api/v1/payroll?month=${month}`) });
  const save = useMutation({
    mutationFn: (patch: Partial<Settings>) => { const s = taxes.data!.settings; return api.put('api/v1/taxes/settings', { vatPeriod: s.vatPeriod, employees: s.employees, dividends: s.dividends, euSupplies: s.euSupplies, psdMembers: s.psdMembers, ...patch }); },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['taxes'] }),
  });
  const s = taxes.data?.settings;
  const run = payroll.data?.run;
  const download = async (url: string, name: string) => {
    setError(null);
    try {
      const r = await fetch(url, { credentials: 'same-origin' });
      if (!r.ok) { const e = await r.json().catch(() => null) as { error?: { code?: string } } | null; throw new ApiError(r.status, e?.error?.code ?? 'server_error'); }
      const href = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = href; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (e) { setError(e); }
  };
  const year = Number(month.slice(0, 4));
  const [saftFrom, setSaftFrom] = useState(() => `${lastMonth().slice(0, 4)}-01`);
  const [saftTo, setSaftTo] = useState(lastMonth);
  const monthEnd = (m: string) => { const [y, mm] = m.split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, mm, 0)).toISOString().slice(0, 10); };
  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Deklaracijos')} text={t('Mėnesio ir metų deklaracijų failai įkėlimui į VMI EDS ir Sodros EDAS. Prieš pateikdamas buhalteris juos patikrina.')} />}>
      <div className="form-actions journal-period">
        <label className="books-date"><span>{t('Mėnuo')}</span><input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} /></label>
      </div>
      {error ? <Alert kind="error">{errorText(t, error)}</Alert> : null}
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{month}</p><h2>{t('Darbo užmokestis ir dividendai')}</h2></div></div>
        <div className="declaration-rows">
          <div className="declaration-row">
            <div><strong>SAM</strong><small>{t('Sodra, iki kito mėnesio 15 d. (EDAS)')}</small></div>
            {run?.status === 'approved'
              ? <button type="button" className="button button--ghost button--small" onClick={() => void download(`api/v1/payroll/${run.id}/sam.ffdata`, `SAM_${month}.ffdata`)}>{t('Atsisiųsti')}</button>
              : <span className="muted">{t('Nėra patvirtintų šio mėnesio atlyginimų.')}</span>}
          </div>
          <div className="declaration-row">
            <div><strong>GPM313</strong><small>{t('VMI, iki kito mėnesio 15 d. (EDS) – pagal išmokėjimo dieną')}</small></div>
            <button type="button" className="button button--ghost button--small" onClick={() => void download(`api/v1/payroll/gpm313.ffdata?month=${month}`, `GPM313_${month}.ffdata`)}>{t('Atsisiųsti')}</button>
          </div>
        </div>
      </section>
      {s?.vatPayer ? <>
        <section className="panel-card">
          <div className="card-head"><div><p className="eyebrow">{month}</p><h2>i.SAF</h2></div></div>
          <p className="muted">{t('Išrašytų ir gautų sąskaitų registras, iki kito mėnesio 20 d.')}</p>
          <IsafDownload month={month} />
        </section>
        <VatReturn halfYear={s.vatPeriod === 'half_year'} deduction={s.vatDeductionPercent ?? 100} onDeduction={(v) => save.mutate({ vatDeductionPercent: v })} />
      </> : null}
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{t('VMI prašymu')}</p><h2>i.SAF-T</h2></div></div>
        <p className="muted">{t('Standartinė apskaitos duomenų rinkmena (SAF-T): sąskaitų planas su likučiais, pirkėjai, tiekėjai, visi laikotarpio įrašai ir sąskaitos faktūros. Teikiama, kai VMI paprašo; laikotarpis – vienų finansinių metų ribose.')}</p>
        <div className="form-actions journal-period">
          <label className="books-date"><span>{t('Nuo')}</span><input type="month" value={saftFrom} onChange={(e) => e.target.value && setSaftFrom(e.target.value)} /></label>
          <label className="books-date"><span>{t('Iki')}</span><input type="month" value={saftTo} onChange={(e) => e.target.value && setSaftTo(e.target.value)} /></label>
          <button type="button" className="button button--ghost button--small" disabled={saftFrom > saftTo || saftFrom.slice(0, 4) !== saftTo.slice(0, 4)}
            onClick={() => void download(`api/v1/books/saft.xml?from=${saftFrom}-01&to=${monthEnd(saftTo)}`, `SAF-T_${saftFrom}_${saftTo}.xml`)}>{t('Atsisiųsti')}</button>
        </div>
      </section>
      <section className="panel-card">
        <div className="card-head"><div><p className="eyebrow">{year - 1}</p><h2>{t('Metinės deklaracijos ir ataskaitos')}</h2></div></div>
        <div className="declaration-rows">
          <div className="declaration-row"><div><strong>PLN204</strong><small>{t('Pelno mokesčio deklaracija, iki birželio 15 d. (EDS)')}</small></div>
            <a className="button button--ghost button--small" href="books?tab=annual">{t('Atidaryti')}</a></div>
          <div className="declaration-row"><div><strong>{t('Finansinės ataskaitos')}</strong><small>{t('Registrų centrui, per 30 d. nuo patvirtinimo (paprastai iki gegužės 30 d.)')}</small></div>
            <a className="button button--ghost button--small" href="books?tab=annual">{t('Atidaryti')}</a></div>
        </div>
      </section>
    </PanelShell>
  );
}
